import { BoundingBox, PnidEquipment, PnidNode } from '../types';

export interface Point {
  x: number;
  y: number;
}

export interface LineSegment {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  kind?: string;
  confidence?: number;
}

export interface MergedLineSegment extends Omit<LineSegment, 'id'> {
  /**
   * Stable representative ID for the merged segment (defaults to min(source ids)).
   * This is meant for UI identity (selection/highlight), not for persistence.
   */
  id: string;
  /** Original segment ids that were merged into this one. */
  source_segment_ids: string[];
  /** Number of raw segments merged. */
  merged_count: number;
  /** Max confidence among merged segments (if provided). */
  merged_confidence_max?: number;
}

export interface MergeLineSegmentsOptions {
  /** Max angular difference (degrees) to consider segments colinear. */
  angleThresholdDeg?: number;
  /** Max average perpendicular distance (px) to consider segments on same line. */
  distanceThresholdPx?: number;
  /** Minimum 1D overlap ratio (intersection / min length) along axis to merge. */
  overlapThreshold?: number;
  /** If endpoints are within this distance (px), allow merging even with low overlap. */
  endpointDistancePx?: number;
  /** Ignore segments shorter than this length (px) for merging; they return as-is. */
  minLengthPx?: number;
}

export interface DetectionCandidate {
  ref: string;
  type: 'equipment' | 'node';
  id: string;
  distance: number;
  confidence: number;
}

/**
 * Check if a point is inside a bounding box
 */
export function isPointInBbox(point: Point, bbox: BoundingBox): boolean {
  return (
    point.x >= bbox.x1 &&
    point.x <= bbox.x2 &&
    point.y >= bbox.y1 &&
    point.y <= bbox.y2
  );
}

/**
 * Calculate distance from a point to a bounding box edge.
 * Returns 0 if point is inside the bbox.
 */
export function pointToBboxDistance(point: Point, bbox: BoundingBox): number {
  if (isPointInBbox(point, bbox)) return 0;

  // Find closest point on bbox boundary
  const closestX = Math.max(bbox.x1, Math.min(point.x, bbox.x2));
  const closestY = Math.max(bbox.y1, Math.min(point.y, bbox.y2));

  return Math.sqrt((point.x - closestX) ** 2 + (point.y - closestY) ** 2);
}

/**
 * Calculate distance from a point to a polyline (minimum distance to any segment)
 */
export function pointToPolylineDistance(point: Point, polyline: number[]): number {
  if (polyline.length < 4) return Infinity;

  let minDist = Infinity;

  for (let i = 0; i < polyline.length - 2; i += 2) {
    const x1 = polyline[i];
    const y1 = polyline[i + 1];
    const x2 = polyline[i + 2];
    const y2 = polyline[i + 3];

    const dist = pointToSegmentDistance(point, { x: x1, y: y1 }, { x: x2, y: y2 });
    minDist = Math.min(minDist, dist);
  }

  return minDist;
}

/**
 * Calculate distance from a point to a line segment
 */
function pointToSegmentDistance(point: Point, segStart: Point, segEnd: Point): number {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // Segment is a point
    return Math.sqrt((point.x - segStart.x) ** 2 + (point.y - segStart.y) ** 2);
  }

  // Project point onto line, clamped to segment
  let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const projX = segStart.x + t * dx;
  const projY = segStart.y + t * dy;

  return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
}

/**
 * Check if a line segment intersects a bounding box
 */
function segmentIntersectsBbox(
  x1: number, y1: number, x2: number, y2: number,
  bbox: BoundingBox
): boolean {
  // Check if either endpoint is inside bbox
  if ((x1 >= bbox.x1 && x1 <= bbox.x2 && y1 >= bbox.y1 && y1 <= bbox.y2) ||
      (x2 >= bbox.x1 && x2 <= bbox.x2 && y2 >= bbox.y1 && y2 <= bbox.y2)) {
    return true;
  }
  
  // Check if segment crosses any bbox edge using line-line intersection
  const bboxEdges = [
    [bbox.x1, bbox.y1, bbox.x2, bbox.y1], // top
    [bbox.x2, bbox.y1, bbox.x2, bbox.y2], // right
    [bbox.x1, bbox.y2, bbox.x2, bbox.y2], // bottom
    [bbox.x1, bbox.y1, bbox.x1, bbox.y2], // left
  ];
  
  for (const [ex1, ey1, ex2, ey2] of bboxEdges) {
    if (segmentsIntersect(x1, y1, x2, y2, ex1, ey1, ex2, ey2)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if two line segments intersect
 */
function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number
): boolean {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (Math.abs(denom) < 1e-10) return false; // parallel
  
  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;
  
  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

/**
 * Check if a polyline intersects (passes through) a bounding box
 */
export function polylineIntersectsBbox(polyline: number[], bbox: BoundingBox): boolean {
  if (polyline.length < 4) return false;
  
  for (let i = 0; i < polyline.length - 2; i += 2) {
    const x1 = polyline[i];
    const y1 = polyline[i + 1];
    const x2 = polyline[i + 2];
    const y2 = polyline[i + 3];
    
    if (segmentIntersectsBbox(x1, y1, x2, y2, bbox)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if a point is near a polyline endpoint (start or end)
 */
export function isNearPolylineEndpoint(
  point: Point,
  polyline: number[],
  threshold: number
): 'start' | 'end' | null {
  if (polyline.length < 2) return null;

  const startDist = Math.sqrt(
    (point.x - polyline[0]) ** 2 + (point.y - polyline[1]) ** 2
  );
  const endDist = Math.sqrt(
    (point.x - polyline[polyline.length - 2]) ** 2 +
    (point.y - polyline[polyline.length - 1]) ** 2
  );

  if (startDist <= threshold && startDist < endDist) return 'start';
  if (endDist <= threshold) return 'end';
  return null;
}

/**
 * Detect which equipment or node a point overlaps with.
 * Returns the best candidate based on proximity.
 * 
 * Priority: Nodes with very close overlap (distance <= nodePreferenceThreshold) 
 * are preferred over equipment, even if equipment bbox contains the point.
 */
export function detectOverlappingEntity(
  point: Point,
  equipments: PnidEquipment[],
  nodes: PnidNode[],
  options: {
    bboxThreshold?: number;           // Max distance to bbox edge (default 50)
    polylineThreshold?: number;       // Max distance to node polyline (default 30)
    nodePreferenceThreshold?: number; // Distance below which nodes beat equipment (default 15)
  } = {}
): DetectionCandidate | null {
  const { bboxThreshold = 50, polylineThreshold = 30, nodePreferenceThreshold = 15 } = options;

  const candidates: DetectionCandidate[] = [];

  // Check nodes FIRST - nodes with very close overlap get highest priority
  for (const node of nodes) {
    if (!node.polyline || node.polyline.length < 4) continue;

    // Check distance to polyline
    const distance = pointToPolylineDistance(point, node.polyline);
    
    if (distance <= polylineThreshold) {
      // Nodes with very close overlap (distance <= nodePreferenceThreshold) get top confidence
      // This ensures they beat equipment even when point is inside equipment bbox
      let confidence: number;
      if (distance <= nodePreferenceThreshold) {
        // Perfect/very close node match - highest priority (1.1 to beat equipment's 1.0)
        confidence = 1.1 - (distance / nodePreferenceThreshold) * 0.05;
      } else {
        // Further node match - standard confidence
        confidence = Math.max(0.4, 0.8 - distance / (polylineThreshold * 2));
      }
      
      candidates.push({
        ref: `Node #${node.node_no}`,
        type: 'node',
        id: node.id,
        distance,
        confidence,
      });
    }
  }

  // Check equipments with bboxes
  for (const eq of equipments) {
    if (!eq.bbox) continue;

    const distance = pointToBboxDistance(point, eq.bbox);
    if (distance <= bboxThreshold) {
      // Confidence: 1.0 if inside, decreasing linearly to 0.5 at threshold
      const confidence = distance === 0 ? 1.0 : Math.max(0.5, 1 - distance / (bboxThreshold * 2));
      candidates.push({
        ref: eq.equipment_no,
        type: 'equipment',
        id: eq.id,
        distance,
        confidence,
      });
    }
  }

  if (candidates.length === 0) return null;

  // Sort by confidence (desc), then by distance (asc)
  candidates.sort((a, b) => {
    if (b.confidence !== a.confidence) return b.confidence - a.confidence;
    return a.distance - b.distance;
  });

  return candidates[0];
}

/**
 * Get all potential candidates for a point (for showing alternatives)
 */
export function getAllOverlappingEntities(
  point: Point,
  equipments: PnidEquipment[],
  nodes: PnidNode[],
  options: {
    bboxThreshold?: number;
    polylineThreshold?: number;
    maxResults?: number;
  } = {}
): DetectionCandidate[] {
  const { bboxThreshold = 80, polylineThreshold = 50, maxResults = 5 } = options;

  const candidates: DetectionCandidate[] = [];

  for (const eq of equipments) {
    if (!eq.bbox) continue;
    const distance = pointToBboxDistance(point, eq.bbox);
    if (distance <= bboxThreshold) {
      const confidence = distance === 0 ? 1.0 : Math.max(0.3, 1 - distance / (bboxThreshold * 2));
      candidates.push({
        ref: eq.equipment_no,
        type: 'equipment',
        id: eq.id,
        distance,
        confidence,
      });
    }
  }

  for (const node of nodes) {
    if (!node.polyline || node.polyline.length < 4) continue;
    const distance = pointToPolylineDistance(point, node.polyline);
    if (distance <= polylineThreshold) {
      const confidence = Math.max(0.3, 0.8 - distance / (polylineThreshold * 2));
      candidates.push({
        ref: `Node #${node.node_no}`,
        type: 'node',
        id: node.id,
        distance,
        confidence,
      });
    }
  }

  candidates.sort((a, b) => {
    if (b.confidence !== a.confidence) return b.confidence - a.confidence;
    return a.distance - b.distance;
  });

  return candidates.slice(0, maxResults);
}

function degreesToRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

function segmentLength(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  return Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
}

function segmentAngleRad(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  // Treat direction as irrelevant: map angle to [0, PI).
  const a = Math.atan2(seg.y2 - seg.y1, seg.x2 - seg.x1);
  return a < 0 ? a + Math.PI : a;
}

function angleDiffRad(a: number, b: number): number {
  // Both in [0, PI). Smallest diff on circle of PI.
  const d = Math.abs(a - b);
  return Math.min(d, Math.PI - d);
}

function distancePointToInfiniteLine(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const denom = dx * dx + dy * dy;
  if (denom === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom;
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}

function segmentMidpoint(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): Point {
  return { x: (seg.x1 + seg.x2) / 2, y: (seg.y1 + seg.y2) / 2 };
}

function unitDirection(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): Point {
  const dx = seg.x2 - seg.x1;
  const dy = seg.y2 - seg.y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return { x: 1, y: 0 };
  return { x: dx / len, y: dy / len };
}

function projectIntervalOntoAxis(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>, axis: Point): [number, number] {
  const p1 = seg.x1 * axis.x + seg.y1 * axis.y;
  const p2 = seg.x2 * axis.x + seg.y2 * axis.y;
  return p1 < p2 ? [p1, p2] : [p2, p1];
}

function overlapRatioOnAxis(
  a: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>,
  b: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>,
  axis: Point
): number {
  const [a0, a1] = projectIntervalOntoAxis(a, axis);
  const [b0, b1] = projectIntervalOntoAxis(b, axis);
  const inter = Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
  const lenA = Math.max(1e-9, a1 - a0);
  const lenB = Math.max(1e-9, b1 - b0);
  return inter / Math.min(lenA, lenB);
}

function endpointDistanceMin(
  a: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>,
  b: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>
): number {
  const a1 = { x: a.x1, y: a.y1 };
  const a2 = { x: a.x2, y: a.y2 };
  const b1 = { x: b.x1, y: b.y1 };
  const b2 = { x: b.x2, y: b.y2 };
  const d11 = Math.hypot(a1.x - b1.x, a1.y - b1.y);
  const d12 = Math.hypot(a1.x - b2.x, a1.y - b2.y);
  const d21 = Math.hypot(a2.x - b1.x, a2.y - b1.y);
  const d22 = Math.hypot(a2.x - b2.x, a2.y - b2.y);
  return Math.min(d11, d12, d21, d22);
}

class UnionFind {
  private parent: number[];
  private rank: number[];

  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
    this.rank = Array.from({ length: n }, () => 0);
  }

  find(x: number): number {
    if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]);
    return this.parent[x];
  }

  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return;
    if (this.rank[ra] < this.rank[rb]) {
      this.parent[ra] = rb;
    } else if (this.rank[ra] > this.rank[rb]) {
      this.parent[rb] = ra;
    } else {
      this.parent[rb] = ra;
      this.rank[ra] += 1;
    }
  }

  groups(): Map<number, number[]> {
    const m = new Map<number, number[]>();
    for (let i = 0; i < this.parent.length; i++) {
      const r = this.find(i);
      const arr = m.get(r);
      if (arr) arr.push(i);
      else m.set(r, [i]);
    }
    return m;
  }
}

function pcaMergeCluster(cluster: LineSegment[]): Omit<MergedLineSegment, 'id' | 'source_segment_ids'> {
  const points: Array<{ x: number; y: number; w: number }> = [];
  for (const s of cluster) {
    const w = Math.max(0.0001, s.confidence ?? 1);
    points.push({ x: s.x1, y: s.y1, w });
    points.push({ x: s.x2, y: s.y2, w });
  }

  // Weighted centroid
  let sumW = 0;
  let meanX = 0;
  let meanY = 0;
  for (const p of points) {
    sumW += p.w;
    meanX += p.w * p.x;
    meanY += p.w * p.y;
  }
  meanX /= sumW;
  meanY /= sumW;

  // Weighted covariance
  let a = 0;
  let b = 0;
  let c = 0;
  for (const p of points) {
    const dx = p.x - meanX;
    const dy = p.y - meanY;
    a += p.w * dx * dx;
    b += p.w * dx * dy;
    c += p.w * dy * dy;
  }
  a /= sumW;
  b /= sumW;
  c /= sumW;

  // Principal axis from covariance eigenvector (2x2)
  let axis: Point;
  if (Math.abs(b) < 1e-12) {
    axis = a >= c ? { x: 1, y: 0 } : { x: 0, y: 1 };
  } else {
    const theta = 0.5 * Math.atan2(2 * b, a - c);
    axis = { x: Math.cos(theta), y: Math.sin(theta) };
  }

  // Project points to get extents
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const p of points) {
    const t = (p.x - meanX) * axis.x + (p.y - meanY) * axis.y;
    tMin = Math.min(tMin, t);
    tMax = Math.max(tMax, t);
  }

  const x1 = meanX + axis.x * tMin;
  const y1 = meanY + axis.y * tMin;
  const x2 = meanX + axis.x * tMax;
  const y2 = meanY + axis.y * tMax;

  // Carry kind as the most frequent (tie-break: first)
  const kindCounts = new Map<string, number>();
  for (const s of cluster) {
    if (!s.kind) continue;
    kindCounts.set(s.kind, (kindCounts.get(s.kind) ?? 0) + 1);
  }
  let kind: string | undefined;
  let best = 0;
  for (const [k, n] of kindCounts.entries()) {
    if (n > best) {
      best = n;
      kind = k;
    }
  }

  const mergedConfidenceMax = cluster.reduce((mx, s) => Math.max(mx, s.confidence ?? -Infinity), -Infinity);

  return {
    x1,
    y1,
    x2,
    y2,
    kind,
    confidence: Number.isFinite(mergedConfidenceMax) ? mergedConfidenceMax : undefined,
    merged_count: cluster.length,
    merged_confidence_max: Number.isFinite(mergedConfidenceMax) ? mergedConfidenceMax : undefined,
  };
}

function stableMergedId(sourceIds: string[]): string {
  // Prefer numeric ordering for IDs like "LSEG-00123", else lexicographic.
  const parsed = sourceIds
    .map((id) => {
      const m = id.match(/(\d+)(?!.*\d)/);
      return { id, n: m ? Number(m[1]) : null };
    })
    .sort((a, b) => {
      if (a.n !== null && b.n !== null && a.n !== b.n) return a.n - b.n;
      if (a.n !== null && b.n === null) return -1;
      if (a.n === null && b.n !== null) return 1;
      return a.id.localeCompare(b.id);
    });
  return parsed[0]?.id ?? sourceIds[0] ?? 'merged';
}

/**
 * Merge overlapping/duplicated AI-extracted line segments into distinct segments for display.
 *
 * This is intended to reduce noise where the AI returns multiple nearly-identical segments
 * stacked on top of each other for the same physical process line.
 *
 * Approach:
 * - Build a similarity graph: segments are linked if they are (1) nearly parallel, (2) close in
 *   perpendicular distance, and (3) overlap substantially along their axis (or have very close endpoints).
 * - Use union-find to form clusters.
 * - For each cluster, compute a statistically merged representative using weighted PCA over endpoints.
 */
export function mergeOverlappingLineSegments(
  segments: LineSegment[],
  options: MergeLineSegmentsOptions = {}
): MergedLineSegment[] {
  const {
    angleThresholdDeg = 6,
    distanceThresholdPx = 6,
    overlapThreshold = 0.7,
    endpointDistancePx = 10,
    minLengthPx = 3,
  } = options;

  if (segments.length <= 1) {
    return segments.map((s) => ({
      ...s,
      source_segment_ids: [s.id],
      merged_count: 1,
      merged_confidence_max: s.confidence,
    }));
  }

  // Precompute metrics
  const segInfo = segments.map((s) => ({
    seg: s,
    len: segmentLength(s),
    ang: segmentAngleRad(s),
    mid: segmentMidpoint(s),
    axis: unitDirection(s),
  }));

  const uf = new UnionFind(segments.length);
  const angleThreshold = degreesToRadians(angleThresholdDeg);

  for (let i = 0; i < segInfo.length; i++) {
    const si = segInfo[i];
    if (si.len < minLengthPx) continue;

    for (let j = i + 1; j < segInfo.length; j++) {
      const sj = segInfo[j];
      if (sj.len < minLengthPx) continue;

      if (angleDiffRad(si.ang, sj.ang) > angleThreshold) continue;

      const lineDist = (distancePointToInfiniteLine(sj.mid, { x: si.seg.x1, y: si.seg.y1 }, { x: si.seg.x2, y: si.seg.y2 }) +
        distancePointToInfiniteLine(si.mid, { x: sj.seg.x1, y: sj.seg.y1 }, { x: sj.seg.x2, y: sj.seg.y2 })) / 2;
      if (lineDist > distanceThresholdPx) continue;

      const ov = overlapRatioOnAxis(si.seg, sj.seg, si.axis);
      const endDist = endpointDistanceMin(si.seg, sj.seg);
      if (ov >= overlapThreshold || endDist <= endpointDistancePx) {
        uf.union(i, j);
      }
    }
  }

  const merged: MergedLineSegment[] = [];
  for (const indices of uf.groups().values()) {
    const cluster = indices.map((idx) => segments[idx]);
    if (cluster.length === 1) {
      const s = cluster[0];
      merged.push({
        ...s,
        source_segment_ids: [s.id],
        merged_count: 1,
        merged_confidence_max: s.confidence,
      });
      continue;
    }

    const sourceIds = cluster.map((s) => s.id);
    const repr = pcaMergeCluster(cluster);
    merged.push({
      id: stableMergedId(sourceIds),
      ...repr,
      source_segment_ids: sourceIds,
    });
  }

  // Stable order: longer first (helps UI scan), then id.
  merged.sort((a, b) => {
    const la = segmentLength(a);
    const lb = segmentLength(b);
    if (lb !== la) return lb - la;
    return a.id.localeCompare(b.id);
  });

  return merged;
}
